import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import JobDetails from './JobDetails';
import BDPerformanceChart from './BDPerformanceChart';
import '../styles/components/insights.css';

/* -----------------------------
   Small presentational helpers
   ----------------------------- */

const Avatar = ({ name }) => {
  const initials = (name || '?')
    .split(' ')
    .map((p) => p.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return <span className="bd-cell__avatar">{initials}</span>;
};

const KpiCard = ({ label, value, sub, tone }) => (
  <div className={`kpi-card ${tone ? `kpi-${tone}` : ''}`}>
    <p className="kpi-label">{label}</p>
    <p className="kpi-value">{value}</p>
    {sub && <p className="kpi-sub">{sub}</p>}
  </div>
);

const StatusMini = ({ bd }) => (
  <div className="status-mini">
    {bd.converted_count > 0 && (
      <span className="status-mini__item converted" title="Converted">
        ✓ {bd.converted_count}
      </span>
    )}
    {bd.offer_awaiting_count > 0 && (
      <span className="status-mini__item offer_awaiting" title="Offer awaiting">
        ⧗ {bd.offer_awaiting_count}
      </span>
    )}
    {bd.waiting_count > 0 && (
      <span className="status-mini__item waiting" title="Waiting">
        ⋯ {bd.waiting_count}
      </span>
    )}
    {!bd.converted_count && !bd.waiting_count && !bd.offer_awaiting_count && (
      <span className="status-mini__item waiting">—</span>
    )}
  </div>
);

const RankCell = ({ rank }) => {
  if (rank > 3) return <span className="rank">#{rank}</span>;
  return <span className={`rank-medal m${rank}`}>{rank}</span>;
};

/* -----------------------------
   Main component
   ----------------------------- */

const Insights = () => {
  const [period, setPeriod] = useState('month');
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [bdStats, setBdStats] = useState([]);
  const [periodAggregates, setPeriodAggregates] = useState({
    total_jobs: 0,
    total_interviews: 0,
    total_converted: 0,
    total_rejected: 0,
  });
  const [summary, setSummary] = useState(null);
  const [periodDates, setPeriodDates] = useState({ start: null, end: null });
  const [loading, setLoading] = useState(true);

  const [selectedBD, setSelectedBD] = useState(null);
  const [bdJobs, setBdJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // Collapsed-preview toggles ("See all")
  const [showAllChart, setShowAllChart] = useState(false);
  const [showAllLeaderboard, setShowAllLeaderboard] = useState(false);
  const PREVIEW_ROWS = 6;

  useEffect(() => {
    fetchInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, startDate, endDate, useCustomRange]);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const [performanceData, summaryData] = await Promise.all([
        useCustomRange && startDate && endDate
          ? api.getBDPerformance(null, startDate, endDate)
          : api.getBDPerformance(period),
        api.getInsightsSummary(),
      ]);

      setBdStats(performanceData.bd_statistics || []);
      setPeriodAggregates({
        total_jobs: performanceData.total_jobs || 0,
        total_interviews: performanceData.total_interviews || 0,
        total_converted: performanceData.total_converted || 0,
        total_rejected: performanceData.total_rejected || 0,
      });
      setSummary(summaryData);
      setPeriodDates({
        start: performanceData.period_start,
        end: performanceData.period_end,
      });
    } catch (error) {
      console.error('Failed to fetch insights:', error);
    } finally {
      setLoading(false);
    }
  };

  /* ---- helpers ---- */

  const formatDateRange = (startStr, endStr) => {
    if (!startStr || !endStr) return '';
    const s = new Date(startStr);
    const e = new Date(endStr);
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', opts)}`;
  };

  const getPeriodLabel = () => {
    if (useCustomRange && startDate && endDate) return 'Custom range';
    switch (period) {
      case '24h':   return 'Last 24 hours';
      case 'week':  return 'Last 7 days';
      case 'month': return 'Last 30 days';
      case 'year':  return 'Last 12 months';
      default:      return '';
    }
  };

  const maxJobs = useMemo(() => {
    if (!bdStats.length) return 1;
    const m = Math.max(...bdStats.map((s) => s.job_count || 0));
    return m === 0 ? 1 : m;
  }, [bdStats]);

  const handleJobCountClick = async (bdName) => {
    setSelectedBD(bdName);
    setLoadingJobs(true);
    try {
      const data = await api.getBDJobs(bdName, period);
      setBdJobs(data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch BD jobs:', error);
      setBdJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  const closeModal = () => {
    setSelectedBD(null);
    setBdJobs([]);
  };

  const handleJobClick = (job) => {
    setSelectedJob({
      id: job.id,
      companyName: job.company_name,
      position: job.position,
      status: job.status,
      jobType: job.job_type,
      payRange: job.pay_range,
      createdAt: job.created_at,
      bdName: selectedBD,
      resumeLink: job.resume_link,
      jdLink: job.jd_link,
    });
  };

  const formatDate = (s) =>
    s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';

  /* ---- derived aggregates for the current period ---- */
  const periodStats = useMemo(() => {
    const totalJobs = periodAggregates.total_jobs || 0;
    const converted = periodAggregates.total_converted || 0;
    const rejected = periodAggregates.total_rejected || 0;
    const decided = converted + rejected;
    const convRate = decided ? (converted / decided) * 100 : 0;
    const activeBds = bdStats.filter((b) => (b.job_count || 0) > 0).length;
    const totalInterviews = periodAggregates.total_interviews || 0;
    const avgRounds =
      totalJobs > 0
        ? bdStats.reduce((sum, b) => sum + (b.avg_rounds_per_job || 0) * (b.job_count || 0), 0) / totalJobs
        : 0;
    return {
      totalJobs,
      totalInterviews,
      converted,
      rejected,
      convRate,
      activeBds,
      avgRounds,
    };
  }, [periodAggregates, bdStats]);

  /* ---- render ---- */

  return (
    <div className="insights-container">
      <div className="page-header">
        <div className="page-header-text">
          <h1>Performance Insights</h1>
          <p>Track jobs, interviews, and conversions across your BD team.</p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="insights-filter-bar">
        <div className="insights-filter-bar__left">
          <span className="insights-filter-bar__label">Period</span>
          {!useCustomRange ? (
            <div className="insights-segment" role="tablist" aria-label="Period">
              {[
                { k: '24h',   l: '24H' },
                { k: 'week',  l: 'Week' },
                { k: 'month', l: 'Month' },
                { k: 'year',  l: 'Year' },
              ].map((p) => (
                <button
                  key={p.k}
                  type="button"
                  role="tab"
                  aria-selected={period === p.k}
                  className={`insights-segment__btn ${period === p.k ? 'is-active' : ''}`}
                  onClick={() => setPeriod(p.k)}
                >
                  {p.l}
                </button>
              ))}
            </div>
          ) : (
            <div className="insights-date-range">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate || undefined}
                aria-label="Start date"
              />
              <span className="insights-filter-bar__period">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
                aria-label="End date"
              />
            </div>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setUseCustomRange((v) => !v)}
          >
            {useCustomRange ? 'Use quick filters' : 'Custom range'}
          </button>
        </div>

        <div className="insights-filter-bar__period">
          {getPeriodLabel()}
          {periodDates.start && periodDates.end && (
            <>  ·  {formatDateRange(periodDates.start, periodDates.end)}</>
          )}
        </div>
      </div>

      {/* KPI cards */}
      <div className="kpi-grid">
        <KpiCard
          tone="primary"
          label="Jobs in period"
          value={periodStats.totalJobs.toLocaleString()}
          sub={`of ${summary ? summary.total_jobs.toLocaleString() : '—'} total`}
        />
        <KpiCard
          tone="info"
          label="Interviews scheduled"
          value={periodStats.totalInterviews.toLocaleString()}
          sub={`avg ${periodStats.avgRounds.toFixed(1)} rounds / job`}
        />
        <KpiCard
          tone="success"
          label="Jobs completed"
          value={periodStats.converted.toLocaleString()}
          sub={
            periodStats.totalJobs
              ? `${((periodStats.converted / periodStats.totalJobs) * 100).toFixed(1)}% of jobs in period`
              : '—'
          }
        />
        <KpiCard
          tone="violet"
          label="Active BDs"
          value={`${periodStats.activeBds}/${summary ? summary.total_bds : bdStats.length}`}
          sub="Contributing in this period"
        />
        <KpiCard
          tone="warning"
          label="Top performer"
          value={bdStats[0]?.bd_name || '—'}
          sub={bdStats[0] ? `${bdStats[0].job_count} jobs · ${bdStats[0].converted_count || 0} completed` : ''}
        />
      </div>

      {/* Pipeline + leaders (two-col) */}
      <div className="insights-split">
        <div className="insight-tile">
          <h3>All-time Pipeline</h3>
          <div className="pipeline-stack">
            <div className="pipeline-pill is-waiting">
              <div className="pipeline-pill__value">
                {summary ? (summary.waiting_jobs || 0).toLocaleString() : '—'}
              </div>
              <div className="pipeline-pill__label">Waiting</div>
            </div>
            <div className="pipeline-pill is-offer">
              <div className="pipeline-pill__value">
                {summary ? (summary.offer_awaiting_jobs || 0).toLocaleString() : '—'}
              </div>
              <div className="pipeline-pill__label">Offer Awaiting</div>
            </div>
            <div className="pipeline-pill is-converted">
              <div className="pipeline-pill__value">
                {summary ? (summary.converted_jobs || 0).toLocaleString() : '—'}
              </div>
              <div className="pipeline-pill__label">Completed</div>
            </div>
          </div>
          <p
            className="kpi-sub"
            style={{ marginTop: 'var(--space-3)' }}
          >
            Avg interviews / job:{' '}
            <strong style={{ color: 'var(--text-primary)' }}>
              {summary ? summary.avg_interviews_per_job : '—'}
            </strong>
          </p>
        </div>

        <div className="insight-tile">
          <h3>Leaders · {getPeriodLabel()}</h3>
          {bdStats[0] && (
            <div className="top-performer">
              <Avatar name={bdStats[0].bd_name} />
              <div>
                <p className="top-performer__name">{bdStats[0].bd_name}</p>
                <p className="top-performer__meta">
                  {bdStats[0].job_count} jobs · {bdStats[0].converted_count || 0} completed
                </p>
              </div>
            </div>
          )}
          <ol className="mini-leader-list">
            {bdStats.slice(1, 5).map((bd, i) => (
              <li key={bd.bd_name}>
                <span className="r">#{i + 2}</span>
                <span className="n">{bd.bd_name}</span>
                <span className="v">{bd.job_count} jobs</span>
              </li>
            ))}
            {bdStats.length === 0 && (
              <li style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                No BD activity for this period.
              </li>
            )}
          </ol>
        </div>
      </div>

      {/* Chart */}
      <div className="insights-section">
        <div className="insights-section__header">
          <div>
            <h2 className="insights-section__title">Team comparison</h2>
            <p className="insights-section__subtitle">
              Jobs sourced and completed per BD for {getPeriodLabel().toLowerCase()}.
            </p>
          </div>
          <span className="insights-section__meta">
            {periodStats.totalJobs} jobs · {periodStats.totalInterviews} interviews
          </span>
        </div>
        <div
          className={
            showAllChart
              ? 'insights-section__body'
              : 'insights-section__body insights-section__body--fixed'
          }
        >
          {loading ? (
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading data…</p>
            </div>
          ) : (
            <BDPerformanceChart
              bdStats={showAllChart ? bdStats : bdStats.slice(0, PREVIEW_ROWS)}
            />
          )}
        </div>
        {!loading && bdStats.length > PREVIEW_ROWS && (
          <div className="insights-section__footer">
            <span className="insights-section__footer-meta">
              Showing {showAllChart ? bdStats.length : Math.min(PREVIEW_ROWS, bdStats.length)} of {bdStats.length} BDs
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setShowAllChart((v) => !v)}
            >
              {showAllChart ? 'Show less' : 'See all'}
            </button>
          </div>
        )}
      </div>

      {/* Leaderboard table */}
      <div className="insights-section">
        <div className="insights-section__header">
          <div>
            <h2 className="insights-section__title">BD leaderboard</h2>
            <p className="insights-section__subtitle">
              Click a row to see jobs for that BD in this period.
            </p>
          </div>
          <span className="insights-section__meta">
            {bdStats.length} BDs
          </span>
        </div>

        {loading ? (
          <div className="insights-section__body">
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading leaderboard…</p>
            </div>
          </div>
        ) : bdStats.length === 0 ? (
          <div className="insights-section__body">
            <div className="empty-state">
              <div className="empty-state-title">No BDs to show</div>
              <div className="empty-state-desc">
                There are no active Business Development users in the system.
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              className={
                showAllLeaderboard
                  ? 'insights-table-wrap'
                  : 'insights-table-wrap insights-table-wrap--fixed'
              }
            >
              <table className="leaderboard">
                <thead>
                  <tr>
                    <th className="rank">#</th>
                    <th>BD</th>
                    <th className="bar-cell">Share</th>
                    <th className="num">Jobs</th>
                    <th className="num">Interviews</th>
                    <th className="num">Avg Rounds</th>
                    <th>Status mix</th>
                    <th className="num">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllLeaderboard ? bdStats : bdStats.slice(0, PREVIEW_ROWS)).map((bd, idx) => {
                    const pct = (bd.job_count / maxJobs) * 100;
                    return (
                      <tr
                        key={bd.bd_name}
                        onClick={() => handleJobCountClick(bd.bd_name)}
                        title="View jobs"
                      >
                        <td className="rank">
                          <RankCell rank={idx + 1} />
                        </td>
                        <td>
                          <div className="bd-cell">
                            <Avatar name={bd.bd_name} />
                            <div>
                              <div className="bd-cell__name">{bd.bd_name}</div>
                              <span className="bd-cell__meta">
                                {bd.share_pct || 0}% of team
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="bar-cell">
                          <div className="mini-bar" aria-label={`${pct.toFixed(0)}% of leader`}>
                            <div className="mini-bar__fill" style={{ width: `${pct}%` }} />
                          </div>
                          <div className="mini-bar__meta">
                            <span>{bd.job_count}</span>
                            <span>{pct.toFixed(0)}%</span>
                          </div>
                        </td>
                        <td className="num">{bd.job_count}</td>
                        <td className="num">{bd.interview_count || 0}</td>
                        <td className="num">{bd.avg_rounds_per_job || 0}</td>
                        <td>
                          <StatusMini bd={bd} />
                        </td>
                        <td className="num">{bd.converted_count || 0}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {bdStats.length > PREVIEW_ROWS && (
              <div className="insights-section__footer">
                <span className="insights-section__footer-meta">
                  Showing {showAllLeaderboard ? bdStats.length : Math.min(PREVIEW_ROWS, bdStats.length)} of {bdStats.length} BDs
                </span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowAllLeaderboard((v) => !v)}
                >
                  {showAllLeaderboard ? 'Show less' : 'See all'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Drilldown modal */}
      {selectedBD && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="bd-jobs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Jobs by {selectedBD} · {getPeriodLabel()}</h3>
              <button className="modal-close" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              {loadingJobs ? (
                <div className="loading-state">
                  <div className="spinner" />
                  <p>Loading jobs…</p>
                </div>
              ) : bdJobs.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-title">No jobs in this period</div>
                  <div className="empty-state-desc">Try selecting a wider date range.</div>
                </div>
              ) : (
                <div className="bd-jobs-list">
                  {bdJobs.map((job) => (
                    <div
                      key={job.id}
                      className="bd-job-item"
                      onClick={() => handleJobClick(job)}
                      title="View job details"
                    >
                      <div className="job-main-info">
                        <span className="job-company">{job.company_name}</span>
                        <span className="job-position">{job.position || ''}</span>
                      </div>
                      <div className="job-meta-info">
                        {job.job_type && <span className="job-type">{job.job_type}</span>}
                        {job.status && (
                          <span className={`status-badge status-${job.status}`}>{job.status}</span>
                        )}
                        <span>{formatDate(job.created_at || job.first_interview_date)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Job details modal */}
      {selectedJob && <JobDetails job={selectedJob} onClose={() => setSelectedJob(null)} />}
    </div>
  );
};

export default Insights;
